"use client";

import ColourComponent from "@JohnLewisPartnership/jl-design-system/dist/components/colour/Colour";
import ColourList from "@JohnLewisPartnership/jl-design-system/dist/components/colour/ColourList";
import Link from "next/link";
import { NextParsedUrlQuery } from "next/dist/server/request-meta";
import { getBaseUrlAndQuery } from "@/utils/url/getBaseUrlAndQuery";

export type ColourOption = {
  /**
   * A string representing the colour.
   */
  colour: string;
  /**
   * A boolean indicating whether to show the colour as available.
   */
  showAsAvailable: boolean;
  /**
   * A string representing the colour swatch. It can also be null or undefined.
   */
  colourSwatch: string | null | undefined;
  /**
   * A string representing the canonical URL. It can also be null or undefined.
   */
  canonicalURL: string | null | undefined;
  /**
   * A boolean indicating whether to show the reduced label.
   */
  showReduced: boolean;
  /**
   * A string representing the reduced label. It can also be null or undefined.
   */
  reducedLabel: string | null | undefined;
  /**
   * A boolean indicating whether the colour is selected.
   */
  selected: boolean;
  /**
   * An object representing a colour's target url
   */
  url: {
    pathname: string;
    query: NextParsedUrlQuery;
  };
};

type Props = {
  /**
   * A number representing the rows to display.
   */
  rowsToDisplay: number;
  /**
   * An array of colour option objects.
   */
  colourOptions: ColourOption[];
};

export const buildColourSwatchUrl = (
  colourSwatchUrl: string | null | undefined,
) => {
  const { baseUrl, query } = getBaseUrlAndQuery(colourSwatchUrl) || [];
  if (baseUrl) {
    const colourSwatchParams = new URLSearchParams(query);
    colourSwatchParams.append("wid", "24");
    colourSwatchParams.append("hei", "24");
    colourSwatchParams.append("fit", "crop");
    return `${baseUrl}?${colourSwatchParams.toString()}`;
  }
  return;
};

export const ColoursUI = ({ rowsToDisplay, colourOptions }: Props) => {
  const selectedColourOption = colourOptions.find((c) => c.selected);

  return (
    <ColourList
      showMore={rowsToDisplay}
      data-testid="colour:option"
      selectedLabel={selectedColourOption?.colour}
    >
      {colourOptions.map((colourOption, index) => {
        return (
          <span
            key={`span${index}`}
            title={colourOption.colour}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore - ignored due to JLDS requirement to have it in children element
            value={colourOption.colour}
          >
            <ColourComponent
              key={`colourOption-${index}`}
              Tag={Link}
              buttonProps={{
                href: colourOption.url,
                shallow: true,
                scroll: false,
              }}
              selected={colourOption.colour === selectedColourOption?.colour}
              unavailable={!colourOption.showAsAvailable}
              name="colourSelector"
              value={colourOption.colour}
              swatch={buildColourSwatchUrl(colourOption.colourSwatch)}
              hasReducedPrice={colourOption.showReduced}
              reducedPriceText={colourOption.reducedLabel}
            />
          </span>
        );
      })}
    </ColourList>
  );
};
